import { type ApiSchemas, call } from '@rental/contracts/client'
import { Temporal } from '@rental/domain'
import { format, formatDateTime, formatEuro, plural } from '@rental/i18n'
import { Button, Field } from '@rental/ui'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { getRouteApi } from '@tanstack/react-router'
import { FileCheck } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import { can } from '../access'
import { useAccess } from '../access-context'
import { api, load } from '../api'
import { DataTable } from '../components/DataTable'
import { EmptyState } from '../components/EmptyState'
import { OutcomeNotice } from '../components/OutcomeNotice'
import { PageHeader } from '../components/PageHeader'
import { PendingLine } from '../components/PendingBadge'
import { ResultCount } from '../components/Results'
import { SearchForm, useSearchFields } from '../components/SearchForm'
import { Section } from '../components/Section'
import { Guard, LoadState } from '../components/states'
import { FieldPair, PAIRED_FIELD, StaffInput } from '../components/ui'
import { FISCAL_QUERY_KEY, RecordDocument } from '../fiscal/Documents'
import { useLocale } from '../i18n'
import { problemOf } from '../problems'
import { BookingCode } from '../reports/BookingCode'
import { useRomeToday } from '../rome-today'

const route = getRouteApi('/fiscal')

type Item = ApiSchemas['FiscalMissingItem']

/** The last 30 days, today included, in Rome: what the server lists when asked nothing. */
export function lastThirtyDays(today: string): { from: string; to: string } {
  return { from: Temporal.PlainDate.from(today).subtract({ days: 29 }).toString(), to: today }
}

/**
 * S6 (CMP-3, decision 3): the Fiscale screen, for those who take money.
 * The till prints the documents; here go the numbers of the money that has
 * none yet, oldest first, by booking code. A document is typed from the row,
 * its printed time starting from when the money moved. Corrections are made
 * on the booking, where each document is shown with its money. UI-2e: the
 * period is in the address, so Back and a link keep it.
 */
export function FiscalScreen() {
  const { locale, messages: m } = useLocale()
  const { access } = useAccess()
  const allowed = can(access, 'payments.record')
  const copy = m.fiscal.missing
  const search = route.useSearch()
  const navigate = route.useNavigate()
  // Today follows the clock (UI-2g). An end asked for alone takes the thirty days up to it,
  // as the server reads it: from today's thirty days, a past end made a range that ended
  // before it began, which the server refused.
  const today = useRomeToday()
  const to = search.to ?? today
  const range = { from: search.from ?? lastThirtyDays(to).from, to }
  // Back, or a link to another period: the fields follow the address.
  const [form, setForm] = useSearchFields(range)
  const [open, setOpen] = useState<string | null>(null)
  // A document typed here: said above the list, which the last one empties along with its
  // pane (UI-2g). Another period is another list: what was done in this one is not said
  // there, nor again on Back.
  const [recorded, setRecorded] = useState<number | null>(null)
  const asked = JSON.stringify(range)
  const [recordedIn, setRecordedIn] = useState(asked)
  if (recordedIn !== asked) {
    setRecordedIn(asked)
    setRecorded(null)
  }
  const missing = useQuery({
    queryKey: [...FISCAL_QUERY_KEY, 'missing', range.from, range.to],
    queryFn: () => load(call(api.GET('/v1/staff/fiscal/missing', { params: { query: range } }))),
    enabled: allowed,
    placeholderData: keepPreviousData,
  })
  const data = missing.data
  const countId = useId()
  const list = useRef<HTMLDivElement>(null)
  const moved = useRef<{ to: 'form' | 'button'; id: string } | null>(null)

  // The form opens in place of its row's button and takes the focus; Annulla gives it back.
  useEffect(() => {
    const target = moved.current
    moved.current = null
    if (!target || !list.current) return
    if (target.to === 'form' && open === target.id) {
      // The first thing to type: the till's point for a card, the number for cash.
      list.current
        .querySelector<HTMLElement>(
          'form select, form input:not([type="radio"]):not([type="checkbox"])',
        )
        ?.focus()
    }
    if (target.to === 'button' && open === null) {
      list.current.querySelector<HTMLElement>(`button[data-record="${target.id}"]`)?.focus()
    }
  }, [open])

  const target = (item: Item) => ({
    money: item.money,
    id: item.id,
    method: item.method,
    amountCents: item.amountCents,
    at: item.at,
    code: item.code,
  })

  return (
    <Guard allowed={allowed} title={m.fiscal.title}>
      <PageHeader title={m.fiscal.title} intro={m.fiscal.intro}>
        <div className="flex flex-col gap-4 pt-2">
          <PendingLine>{m.fiscal.providerPending}</PendingLine>
          <SearchForm
            label={m.fiscal.title}
            submitLabel={m.reports.show}
            filtered={search.from !== undefined || search.to !== undefined}
            onSubmit={() =>
              void navigate({
                to: '/fiscal',
                search: { from: form.from || undefined, to: form.to || undefined },
              })
            }
            onClear={() => void navigate({ to: '/fiscal', search: {} })}
          >
            <FieldPair>
              <Field className={PAIRED_FIELD} label={m.reports.from} required>
                {(control) => (
                  <StaffInput
                    {...control}
                    type="date"
                    value={form.from}
                    onChange={(event) => setForm({ ...form, from: event.target.value })}
                  />
                )}
              </Field>
              <Field className={PAIRED_FIELD} label={m.reports.to} required>
                {(control) => (
                  <StaffInput
                    {...control}
                    type="date"
                    value={form.to}
                    onChange={(event) => setForm({ ...form, to: event.target.value })}
                  />
                )}
              </Field>
            </FieldPair>
          </SearchForm>
          <ResultCount
            id={countId}
            testId="fiscal-totals"
            count={
              data
                ? plural(locale, copy.totals, data.count, {
                    amount: formatEuro(data.totalCents, locale),
                  })
                : null
            }
            range={
              data && data.count > data.items.length
                ? format(copy.shown, { shown: data.items.length, count: data.count })
                : null
            }
            asked={asked}
            fetching={missing.isPlaceholderData}
          />
        </div>
      </PageHeader>

      {recorded === null ? null : (
        <OutcomeNotice key={recorded}>
          <p>{m.fiscal.recorded}</p>
        </OutcomeNotice>
      )}
      <LoadState data={data} problem={problemOf(missing.error)}>
        {(data) =>
          data.items.length === 0 ? (
            <EmptyState icon={FileCheck} title={copy.none} />
          ) : (
            <Section title={copy.title}>
              <div ref={list} aria-busy={missing.isPlaceholderData || undefined}>
                <DataTable
                  label={copy.title}
                  rows={data.items}
                  rowKey={(item) => item.id}
                  rowMarks={(item) => ({ testId: `missing-${item.id}` })}
                  columns={[
                    { header: copy.when, cell: (item) => formatDateTime(locale, item.at) },
                    {
                      header: copy.booking,
                      cell: (item) => <BookingCode code={item.code} />,
                      rowHeader: true,
                    },
                    { header: copy.money, cell: (item) => m.fiscal.money[item.money] },
                    { header: copy.method, cell: (item) => m.fiscal.methods[item.method] },
                    {
                      header: copy.amount,
                      cell: (item) => formatEuro(item.amountCents, locale),
                      numeric: true,
                    },
                    {
                      header: m.fiscal.record,
                      headerHidden: true,
                      cell: (item) =>
                        open === item.id ? null : (
                          // Quiet, as on Cassa's Zs and the booking page: it opens a form under
                          // its row, which the row's button then leaves to (UI-2g).
                          <Button
                            variant="link"
                            data-record={item.id}
                            // Named by its row: a column of "Registra" is no name at all.
                            aria-label={format(m.fiscal.recordFor, {
                              code: item.code,
                              amount: formatEuro(item.amountCents, locale),
                            })}
                            onClick={() => {
                              moved.current = { to: 'form', id: item.id }
                              setRecorded(null)
                              setOpen(item.id)
                            }}
                          >
                            {m.fiscal.recordSubmit}
                          </Button>
                        ),
                    },
                  ]}
                  after={(item) =>
                    open === item.id ? (
                      <RecordDocument
                        target={target(item)}
                        onDone={() => {
                          setOpen(null)
                          setRecorded(Date.now())
                        }}
                        onCancel={() => {
                          moved.current = { to: 'button', id: item.id }
                          setOpen(null)
                        }}
                      />
                    ) : null
                  }
                />
              </div>
            </Section>
          )
        }
      </LoadState>
    </Guard>
  )
}
